import {
  Alert,
  Button,
  LoadingOverlay,
  Notification,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { useMutation, useApolloClient } from "@apollo/client";
import { useForm } from "@mantine/form";
import { useState, useEffect, useRef, useMemo } from "react";
import { CurrencyDollar } from "tabler-icons-react";
import { IconCheck, IconX } from "@tabler/icons";
import { getOrderHandler } from "../../../services/orderHandlers";
import { useMediaQuery } from '@mantine/hooks';
import { SYMBOLS } from "../../../constants/symbols";
import { buildApiUrl } from "../../../config/endpoints";
import "./SpotComponent.css";
import { ADD_LOG } from "../../../queries/user-analytics";
import { useWaveConfig, getEntryFlowType } from "../../../waves/WaveConfigContext";
import ObstructionOpenModal from "../../../waves/obstruction/ObstructionOpenModal";

const modes = [
  {
    mode: "Buy",
    component: <div>Buy</div>,
    color: "green"
  },
  {
    mode: "Sell", 
    component: <div>Sell</div>,
    color: "red"
  },
  {
    mode: "Short",
    component: <div>Short</div>,
    color: "orange"
  },
  {
    mode: "Cover",
    component: <div>Cover</div>, 
    color: "blue"
  },
];

export default function SpotComponent(props) {
  const apolloClient = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const symbol = SYMBOLS.find((x) => x.code === props.symbol);
  const [mode, setMode] = useState(modes[0].mode);
  const [notification, setNotification] = useState(null);
  const isMobile = useMediaQuery('(max-width: 1090px)');
  const containerRef = useRef(null);
  const [activeCoverIntent, setActiveCoverIntent] = useState(null);
  const [activeSellIntent, setActiveSellIntent] = useState(null);
  const [latestPrice, setLatestPrice] = useState(null);
  const borrowRate = symbol?.borrowRate || 0;

  // Wave E — Obstruction Arm C symmetric entry flow. For a Phase 3 Arm C
  // participant, a Buy first runs the 4-step entry obstruction; only on
  // completion does the buy reach the matching engine. `logActivity` records
  // the "trade" research event after any successful order (§5p).
  const { config: waveConfig, logActivity } = useWaveConfig();
  const [entryModal, setEntryModal] = useState({ opened: false, quantity: 0 });
  const entryBypassRef = useRef(false); // set true to skip the gate on re-entry

  const form = useForm({
    initialValues: {
      orderType: "Limit",
      price: symbol.price,
      qty: symbol.min,
    },
  });

  useEffect(() => {
    const fetchLatestPrice = async () => {
      try {
        const response = await fetch(buildApiUrl('/latestprice') + '?' + new URLSearchParams({ symbol: symbol.altcode }));
        if (!response.ok) {
          throw new Error(`Error! status: ${response.status}`);
        }
        const data = await response.json();
        const price = parseFloat(data.latest_price);
        const quantity = 5 / price;
        const roundedQty = parseFloat(quantity.toFixed(9));
        setLatestPrice(price);
        
        // Only update price for limit orders or when not in cover/sell-intent mode.
        // Sell needs the same protection Cover already has here — otherwise this
        // auto-suggested price/quantity immediately clobbers what the sellIntent
        // effect just pre-filled the moment `mode` flips to "Sell".
        if (form.values.orderType === "Limit" || (mode !== "Cover" && mode !== "Sell" && !activeCoverIntent && !activeSellIntent)) {
          form.setFieldValue("price", price);
        }

        // Only update quantity if not in cover/sell-intent mode.
        if (mode !== "Cover" && mode !== "Sell" && !activeCoverIntent && !activeSellIntent) {
          form.setFieldValue("qty", roundedQty);
        }
      } catch (err) {
        console.error("Failed to fetch latest price:", err);
      }
    };
    fetchLatestPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol.altcode, mode, activeCoverIntent, activeSellIntent]);

  // Listen for cover mode trigger from short positions
  useEffect(() => {
    const intent = props.coverIntent;
    console.log('Cover intent received:', intent);
    
    if (!intent) {
      return;
    }

    const normalizedSymbol = intent.symbol?.toUpperCase();
    const normalizedCurrent = props.symbol?.toUpperCase();
    console.log('Symbol check:', { normalizedSymbol, normalizedCurrent });
    
    if (!normalizedSymbol || normalizedSymbol !== normalizedCurrent) {
      props.onCoverIntentHandled?.();
      return;
    }

    const numericQuantity = Number(intent.quantity);
    const numericPositionId = Number(intent.positionId);
    console.log('Parsed values:', { numericQuantity, numericPositionId });
    
    if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
      setNotification(
        <Notification
          color="red"
          title="Cover Mode"
          onClose={() => setNotification(null)}
        >
          Unable to prepare order – invalid position quantity.
        </Notification>
      );
      setTimeout(() => setNotification(null), 3000);
      props.onCoverIntentHandled?.();
      return;
    }
    if (!Number.isInteger(numericPositionId) || numericPositionId <= 0) {
      setNotification(
        <Notification
          color="red"
          title="Cover Mode"
          onClose={() => setNotification(null)}
        >
          Unable to prepare order – invalid position identifier.
        </Notification>
      );
      setTimeout(() => setNotification(null), 3000);
      props.onCoverIntentHandled?.();
      return;
    }

    console.log('Setting cover mode with quantity:', numericQuantity);
    setMode("Cover");
    form.setFieldValue("orderType", "Market");
    form.setFieldValue("qty", numericQuantity);
    form.setFieldValue("price", 0);
    setActiveCoverIntent({
      positionId: numericPositionId,
      quantity: numericQuantity,
      entryPrice: intent.entryPrice,
    });

    setNotification(
      <Notification
        color="blue"
        title="Cover Mode Activated"
        onClose={() => setNotification(null)}
      >
        Review the order details below and click "Place Cover Order" when ready.
      </Notification>
    );
    setTimeout(() => setNotification(null), 4000);

    requestAnimationFrame(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    props.onCoverIntentHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.coverIntent, props.symbol, props.onCoverIntentHandled]);

  useEffect(() => {
    if (mode !== "Cover") {
      setActiveCoverIntent(null);
    }
  }, [mode]);

  // Listen for sell mode trigger from long positions (holdings)
  useEffect(() => {
    const intent = props.sellIntent;
    console.log('Sell intent received:', intent);

    if (!intent) {
      return;
    }

    const normalizedSymbol = intent.symbol?.toUpperCase();
    const normalizedCurrent = props.symbol?.toUpperCase();
    console.log('Symbol check:', { normalizedSymbol, normalizedCurrent });

    if (!normalizedSymbol || normalizedSymbol !== normalizedCurrent) {
      props.onSellIntentHandled?.();
      return;
    }

    const numericQuantity = Number(intent.quantity);
    const numericPositionId = Number(intent.positionId);
    console.log('Parsed values:', { numericQuantity, numericPositionId });

    if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
      setNotification(
        <Notification
          color="red"
          title="Sell Mode"
          onClose={() => setNotification(null)}
        >
          Unable to prepare order – invalid position quantity.
        </Notification>
      );
      setTimeout(() => setNotification(null), 3000);
      props.onSellIntentHandled?.();
      return;
    }
    if (!Number.isInteger(numericPositionId) || numericPositionId <= 0) {
      setNotification(
        <Notification
          color="red"
          title="Sell Mode"
          onClose={() => setNotification(null)}
        >
          Unable to prepare order – invalid position identifier.
        </Notification>
      );
      setTimeout(() => setNotification(null), 3000);
      props.onSellIntentHandled?.();
      return;
    }

    console.log('Setting sell mode with quantity:', numericQuantity);
    setMode("Sell");
    form.setFieldValue("orderType", "Market");
    form.setFieldValue("qty", numericQuantity);
    form.setFieldValue("price", 0);
    setActiveSellIntent({
      positionId: numericPositionId,
      quantity: numericQuantity,
      entryPrice: intent.entryPrice,
    });

    setNotification(
      <Notification
        color="blue"
        title="Sell Mode Activated"
        onClose={() => setNotification(null)}
      >
        Review the order details below and click "Place Sell Order" when ready.
      </Notification>
    );
    setTimeout(() => setNotification(null), 4000);

    requestAnimationFrame(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    props.onSellIntentHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sellIntent, props.symbol, props.onSellIntentHandled]);

  useEffect(() => {
    if (mode !== "Sell") {
      setActiveSellIntent(null);
    }
  }, [mode]);

  // added for tracking user data
  const [logEventMutation] = useMutation(ADD_LOG, {
    onCompleted: ({ addLog }) => {
        // Access the HTTP response directly
        const { status, message } = logEvent;
        if (status === "success") {
            console.log("Event logged successfully:", message);
        } else {
            console.error("Failed to log event:", message);
        }
    },
    onError: (error) => {
        console.error("Error logging event:", error.message);
    }
  });

const logEvent = (eventType, additionalData, _userid) => {
  const eventData = {
      userid: Number(_userid),
      event_type: eventType,
      timestamp: new Date().toISOString(),
      additional_data: JSON.stringify(additionalData)

  };

  logEventMutation({ variables: eventData });
};

  // Message format:
  // -   Add, Symbol, Type, Side, Quantity, Price, Owner ID, Wallet ID, Stop Price (Optional)
  //     add ETHUSD limit ask 100 64000 user1 Alice1 (60000)
  //     add ethusd market ask 100 0 user1 Alice1 (60000)

  // -   Modify, Symbol, Side, Order ID, prev Quantity, prev Price, new Quantity, new Price
  //     modify ETHUSD buy 0000000002 100 63000 100 64000 //change price to 64000 only

  //   - Cancel, Symbol, Side, Price, Order ID
  //     cancel ETHUSD buy 100 0000000001
  // type RequestBody struct {
  // 	Operation    string `json:"operation"`
  // 	Symbol       string `json:"symbol"`
  // 	Type         string `json:"type"`
  // 	Side         string `json:"side"`
  // 	Quantity     string `json:"quantity"`
  // 	Price        string `json:"price"`
  // 	OwnerID      string `json:"owner_id"`
  // 	WalletID     string `json:"wallet_id"`
  // 	OrderID      string `json:"order_id"`
  // 	PrevQuantity string `json:"prev_quantity"`
  // 	PrevPrice    string `json:"prev_Price"`
  // 	NewQuantity  string `json:"new_quantity"`
  // 	NewPrice     string `json:"new_price"`
  // }
  const placeOrderHandler = async () => {
    // Wave O Arm C entry gate: opening a position (Buy or Short) runs the
    // 4-step entry obstruction first. The modal's onComplete/onFallback
    // re-invokes this handler with the gate bypassed so the order proceeds
    // to the engine normally. (§17/§17.5, CHANGE 5.)
    if (!entryBypassRef.current && (mode === "Buy" || mode === "Short") && getEntryFlowType(waveConfig) === "four_step") {
      const qtyNum = Number(form.values.qty);
      if (Number.isFinite(qtyNum) && qtyNum > 0) {
        setEntryModal({ opened: true, quantity: qtyNum });
        return;
      }
      // Invalid quantity — fall through so the normal validation error shows.
    }
    entryBypassRef.current = false;

    setIsLoading(true);
    let wasSuccessful = false;
    const controller = new AbortController();
    // Extend order request timeout to 60s to avoid premature aborts
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);
    setNotification(
      <Notification
        loading
        title="Sending order request to server"
        disallowClose
      >
        Please wait until order is send
      </Notification>
    );
    try {
      // Determine order sub-type and side based on mode
      let orderSubType = "NORMAL";
      let side = mode;
      let sanitizedQuantity = Number(form.values.qty);
      const sanitizedPrice = Number(form.values.price);
      let quantityAdjusted = false;
      let adjustedQuantityNote = "";
      
      if (mode === "Short") {
        orderSubType = "SHORT";
        side = "SELL"; // Short orders are executed as sells
      } else if (mode === "Cover") {
        orderSubType = "COVER";
        side = "BUY"; // Cover orders are executed as buys
      }

      if (!Number.isFinite(sanitizedQuantity) || sanitizedQuantity <= 0) {
        throw new Error("Please enter a valid quantity greater than zero.");
      }

      if (form.values.orderType === "Limit") {
        if (!Number.isFinite(sanitizedPrice) || sanitizedPrice <= 0) {
          throw new Error("Please enter a valid positive price for limit orders.");
        }
      }

      if (orderSubType === "COVER" && activeCoverIntent?.positionId) {
        // Clamp cover quantity to the originally loaded position
        const availableQuantity = activeCoverIntent.quantity;
        if (sanitizedQuantity > availableQuantity) {
          sanitizedQuantity = availableQuantity;
          quantityAdjusted = true;
          adjustedQuantityNote = `Requested quantity exceeded remaining position. Using available quantity ${availableQuantity}.`;
          form.setFieldValue("qty", availableQuantity);
        }
      }

      if (mode === "Sell" && activeSellIntent?.positionId) {
        // Clamp sell quantity to the originally loaded holding
        const availableQuantity = activeSellIntent.quantity;
        if (sanitizedQuantity > availableQuantity) {
          sanitizedQuantity = availableQuantity;
          quantityAdjusted = true;
          adjustedQuantityNote = `Requested quantity exceeded available holding. Using available quantity ${availableQuantity}.`;
          form.setFieldValue("qty", availableQuantity);
        }
      }

      // Format numeric values to fixed precision
      // Quantity: exactly 9 decimal places
      // Price: keep original precision
      const payloadQuantity = sanitizedQuantity.toFixed(9);
      const payloadPrice = sanitizedPrice.toString();
      const requestPayload = {
        symbol: props.symbol,
        type: form.values.orderType,
        side: side,
        quantity: payloadQuantity,
        price: payloadPrice,
        owner_id: props.ownerId.toString(),
        wallet_id: props.walletId.toString(),
        order_sub_type: orderSubType,
      };

      if (orderSubType === "COVER" && activeCoverIntent?.positionId) {
        requestPayload.short_position_id = activeCoverIntent.positionId;
      }

      const response = await fetch(buildApiUrl('/order'), {
        method: "POST",
        body: JSON.stringify(requestPayload),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      if (response.status === 420) {
        logEvent('Error - Insufficient Balance', { symbol: props.symbol, orderType: form.values.orderType, side: mode, quantity: form.values.qty, price: form.values.price }, props.ownerId);
        throw new Error(`Insufficient Balance!`);
      } else if (response.status === 421) {
        logEvent('Error - Insufficient Assets!', { symbol: props.symbol, orderType: form.values.orderType, side: mode, quantity: form.values.qty, price: form.values.price }, props.ownerId); 
        throw new Error(`Insufficient Assets!`);
      } else if (!response.ok) {
        // Try to get the error message from the response
        let errorMessage = `Error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
            // Provide more specific error messages for cover orders
            if (mode === "Cover" && errorMessage.includes("No open short positions")) {
              errorMessage = "No open short positions found to cover for this symbol";
            }
          }
        } catch (e) {
          // If we can't parse the error response, use the default message
        }
        logEvent(`Error - ${errorMessage}`, { symbol: props.symbol, orderType: form.values.orderType, side: mode, quantity: form.values.qty, price: form.values.price }, props.ownerId); 
        throw new Error(errorMessage);
      }

      await response.json();
      
      // Create appropriate success message based on order type
      let successTitle = `Order (${symbol.symbol}) placed successful`;
      let successMessage = "Order is added to the order book";
      const executedQuantityDisplay = sanitizedQuantity.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 12,
      });
      
      if (mode === "Cover") {
        successTitle = `Cover Order (${symbol.symbol}) successful`;
        successMessage = quantityAdjusted
          ? `Short position has been covered using the remaining quantity of ${executedQuantityDisplay}`
          : "Short position has been covered";
        if (quantityAdjusted && adjustedQuantityNote) {
          successMessage += `. ${adjustedQuantityNote}`;
        }
      } else if (mode === "Short") {
        successTitle = `Short Order (${symbol.symbol}) successful`;
        successMessage = "Short position has been opened";
      }
      
      const successNotification = (
        <Notification
          icon={<IconCheck size={18} />}
          color="teal"
          title={successTitle}
          onClose={() => {
            setNotification(null);
          }}
        >
          {successMessage}
        </Notification>
      );
      setNotification(successNotification);
      setTimeout(() => setNotification(null), 5000);
      // console.log("result is: ", JSON.stringify(result, null, 4));
      logEvent(
        `Manual ${form.values.orderType} ${mode} Order - ${symbol.symbol}`,
        {
          symbol: props.symbol,
          orderType: form.values.orderType,
          side: mode,
          quantity: sanitizedQuantity,
          quantityDisplay: executedQuantityDisplay,
          price: sanitizedPrice,
          quantityAdjusted,
        },
        props.ownerId
      );
      wasSuccessful = true;
      // Research activity event — fire-and-forget, never blocks the UI (§5p).
      logActivity("trade");
      if (mode === "Cover") {
        setActiveCoverIntent(null);
        form.reset();
        setMode(modes[0].mode);
        apolloClient.refetchQueries({ include: ["GetShortPositions"] });
      }
    } catch (err) {
      console.error(err);
      let message = err?.message || "Something went wrong while placing the order.";
      if (err?.name === "AbortError") {
        message = "Order request timed out. Please check if the matching engine is running and try again.";
      }
      // Show any backend error for cover explicitly
      if (mode === "Cover" && message.includes("cover")) {
        message = message.replace(/cover/i, "cover order");
      }
      const errorNotification = (
        <Notification
          icon={<IconX size={18} />}
          color="red"
          onClose={() => {
            setNotification(null);
          }}
        >
          {message}
        </Notification>
      );
      setNotification(errorNotification);
      setTimeout(() => setNotification(null), 5000);
    } finally {
      window.clearTimeout(timeoutId);
      setIsLoading(false);
      await Promise.resolve(
        getOrderHandler(
          props.ownerId.toString(),
          props.symbol,
          props.setOpenAskOrders,
          props.setOpenBidOrders,
          props.setClosedOrders
        )
      );
    }
    return wasSuccessful;
  };

  const toggleEditor = () => {
    props.setIsActive((prevIsActive) => !prevIsActive); // Toggle isActive state using the setter function
  };

  const estimatedDailyFee = useMemo(() => {
    if (mode !== "Short") {
      return 0;
    }

    const quantity = Number(form.values.qty);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return 0;
    }

    const referencePrice = form.values.orderType === "Limit"
      ? Number(form.values.price)
      : latestPrice;

    if (!Number.isFinite(referencePrice) || referencePrice <= 0) {
      return 0;
    }

    return (quantity * referencePrice * borrowRate) / 365;
  }, [mode, form.values.qty, form.values.price, form.values.orderType, latestPrice, borrowRate]);

  const formattedDailyFee = estimatedDailyFee > 0
    ? estimatedDailyFee.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";
  const borrowRatePercent = (borrowRate * 100).toFixed(2);
  const isHighFeeAsset = borrowRate >= 0.12;

  const runEntryBypassingGate = () => {
    setEntryModal({ opened: false, quantity: 0 });
    entryBypassRef.current = true;
    placeOrderHandler();
  };

  return (
    <Stack py="xl" spacing={20} ref={containerRef}>
      {notification}
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <ObstructionOpenModal
        opened={entryModal.opened}
        positionSymbol={props.symbol}
        quantity={entryModal.quantity}
        price={form.values.orderType === "Limit" ? Number(form.values.price) : latestPrice}
        onComplete={runEntryBypassingGate}
        onFallbackClose={runEntryBypassingGate}
        onAbandon={() => setEntryModal({ opened: false, quantity: 0 })}
      />
      <SimpleGrid cols={2}>
        {modes.map((m, index) => (
          <Button
            key={index}
            size="sm"
            onClick={() => setMode(m.mode)}
            variant={m.mode === mode ? 'filled' : 'light'}
            color={m.mode === mode ? m.color : 'gray'}
          >
            {m.mode}
          </Button>
        ))}
      </SimpleGrid>
      <Select value="" {...form.getInputProps('orderType')} data={[{ value: 'Limit', label: 'Limit' }, { value: 'Market', label: 'Market' }]} />
      {form.values.orderType === 'Limit' && <NumberInput label="Order Price" description={`per ${symbol.symbol}`} precision={2} min={0.01} step={0.01} max={1000000} icon={<CurrencyDollar size={18} />} {...form.getInputProps('price')} />}
      <NumberInput
        label="Quantity"
        description="min 5 USD"
        precision={9}
        min={symbol.min}
        step={1e-9}
        max={100}
        icon={<div size={18}>{symbol.symbol}</div>}
        iconWidth={60}
        {...form.getInputProps('qty')}
      />
      {mode === "Short" && (
        <Alert
          color={isHighFeeAsset ? "orange" : "blue"}
          title={isHighFeeAsset ? "High Borrowing Fees" : "Borrowing Fees"}
          radius="md"
        >
          <Text size="sm">
            Borrow rate: {borrowRatePercent}% annual · Estimated daily cost ${formattedDailyFee}
          </Text>
          {isHighFeeAsset && (
            <Text size="xs" color="dimmed">
              Consider tighter holding periods or covering sooner to reduce fees.
            </Text>
          )}
        </Alert>
      )}
      <Button 
        size="md" 
        onClick={placeOrderHandler} 
        className="button"  
        style={{ 
          backgroundColor: 
            mode === 'Buy' ? 'green' : 
            mode === 'Sell' ? 'red' : 
            mode === 'Short' ? 'orange' : 
            mode === 'Cover' ? 'blue' : 'gray'
        }}
      >
        Place {mode} Order
      </Button>
      {!isMobile && <Button size="md" onClick={toggleEditor}>
        {props.isActive ? 'Close Editor' : 'Open Editor'}
      </Button>}
    </Stack>
  );
}
